// Central tool registry. Custom icons can be dropped in later by swapping iconKey → component map below.
import {
  Sparkles, Anchor, Wand2, Repeat2, ListChecks, CalendarDays, Layers,
  Mail, Newspaper, Megaphone, Search, Layout, Handshake, FileText, MessageSquareReply,
  GraduationCap, Package, Code2, Share2, Send, Target, Rocket, TrendingUp, Award,
} from "lucide-react";

export const ICONS = {
  reel: Sparkles,
  hook: Anchor,
  caption: Wand2,
  emulation: Repeat2,
  one_dive: ListChecks,
  plan: CalendarDays,
  carousel: Layers,
  email_campaign: Mail,
  newsletter: Newspaper,
  meta_ads: Megaphone,
  google_ads: Search,
  funnel: Layout,
  brand_deal: Handshake,
  blog: FileText,
  review: MessageSquareReply,
  course: GraduationCap,
  gear: Package,
  html_studio: Code2,
  seo_product: Search,
};

// Custom PNG icons uploaded by Robin. Mapped to the 5 flagship tools so the
// Dashboard can pick them up via an IconRenderer that prefers custom over lucide.
export const CUSTOM_ICONS = {
  reel: "/tool-icons/icon1.png",
  carousel: "/tool-icons/icon2.png",
  plan: "/tool-icons/icon3.png",
  meta_ads: "/tool-icons/icon4.png",
  email_campaign: "/tool-icons/icon5.png",
};

export const TOOLS = [
  // Creator tier
  { key: "reel", backendKey: "reel", route: "/tools/reel", title: "Viral Reel Builder", short: "Full reel pack from a brief", icon: "reel", tier: "basic", category: "social" },
  { key: "carousel", backendKey: "viral_carousel", route: "/tools/viral-carousel", title: "Viral Carousel Builder", short: "7-10 slide carousels with caption", icon: "carousel", tier: "basic", category: "social" },
  { key: "emulation", backendKey: "emulation", route: "/tools/emulation", title: "Emulation Mode", short: "Reverse-engineer any viral reel", icon: "emulation", tier: "basic", category: "social" },
  { key: "plan", backendKey: "content_plan", route: "/tools/content-plan", title: "Dive Content Planner", short: "14-day calendar with hooks & CTAs", icon: "plan", tier: "basic", category: "social" },
  { key: "hook", backendKey: "hook_bank", route: "/tools/hook-bank", title: "Hook Bank", short: "On-brand hooks, six archetypes", icon: "hook", tier: "basic", category: "social" },
  { key: "caption", backendKey: "caption_rewrite", route: "/tools/caption-rewrite", title: "Caption Rewrite", short: "DIL doctrine long-form rewrite", icon: "caption", tier: "basic", category: "social" },
  { key: "one_dive", backendKey: "one_dive_five_reels", route: "/tools/one-dive", title: "One Dive = 5 Reels", short: "Five angles from one dive", icon: "one_dive", tier: "basic", category: "social" },
  // Business tier
  { key: "newsletter", backendKey: "email_newsletter", route: "/tools/email-newsletter", title: "Email Newsletter Builder", short: "A polished weekly newsletter", icon: "newsletter", tier: "pro", category: "email" },
  { key: "email_campaign", backendKey: "email_campaign", route: "/tools/email-campaign", title: "Email Marketing Campaign Builder", short: "4-email nurture sequences", icon: "email_campaign", tier: "pro", category: "email" },
  { key: "meta_ads", backendKey: "meta_ads", route: "/tools/meta-ads", title: "Meta Ad Builder", short: "Facebook & Instagram ad creatives", icon: "meta_ads", tier: "pro", category: "ads" },
  { key: "google_ads", backendKey: "google_ads", route: "/tools/google-ads", title: "Google Ads Builder", short: "Search + PMax assets", icon: "google_ads", tier: "pro", category: "ads" },
  { key: "blog", backendKey: "seo_blog", route: "/tools/seo-blog", title: "SEO Blog Writer", short: "Long-form, ranked-ready posts", icon: "blog", tier: "pro", category: "content" },
  { key: "review", backendKey: "review_response", route: "/tools/review-response", title: "Review Response Writer", short: "Three calm response variants", icon: "review", tier: "pro", category: "content" },
  { key: "course", backendKey: "course_promo", route: "/tools/course-promo", title: "Course Promo Builder", short: "Full launch pack for a course", icon: "course", tier: "pro", category: "promo" },
  { key: "gear", backendKey: "gear_promo", route: "/tools/gear-promo", title: "Gear Promo Builder", short: "Product launches & gear posts", icon: "gear", tier: "pro", category: "promo" },
  { key: "funnel", backendKey: "sales_funnel", route: "/tools/sales-funnel", title: "Sales Funnel Copy", short: "Landing page + offer stack", icon: "funnel", tier: "pro", category: "content" },
  { key: "brand_deal", backendKey: "brand_deal", route: "/tools/brand-deal", title: "Brand Deal Pitch", short: "Cold outreach + media kit", icon: "brand_deal", tier: "pro", category: "promo" },
  { key: "seo_product", backendKey: "seo_product_page", route: "/tools/seo-product-page", title: "SEO Product Page Rewrite", short: "Rewrite a page for SEO + conversion", icon: "seo_product", tier: "pro", category: "content" },
  // Premium Unlimited — Branded HTML Studio (email + web). Zero credits.
  { key: "html_studio", backendKey: "html_studio", route: "/tools/html-studio", title: "Branded HTML Studio", short: "Turn content into branded HTML emails & pages", icon: "html_studio", tier: "pro_unlimited", premium: true, unlimited: true, category: "email" },
];

// -------------------------------------------------------------------------
// Intent-based tool navigation. Users first answer "what do you want to do?"
// then see the relevant tools. A tool may appear in more than one category
// (e.g. the HTML Studio serves email, sales and web intents).
// -------------------------------------------------------------------------
export const CATEGORIES = [
  { id: "social",      name: "Create Social Content", icon: Share2,     blurb: "Reels, carousels, hooks, captions and a 14-day plan.", toolKeys: ["reel", "carousel", "emulation", "plan", "hook", "caption", "one_dive"] },
  { id: "email",       name: "Email Your Audience",   icon: Send,       blurb: "Newsletters, nurture sequences and branded HTML emails.", toolKeys: ["newsletter", "email_campaign", "html_studio"] },
  { id: "advertising", name: "Advertise",             icon: Target,     blurb: "Meta and Google ad creative that converts.", toolKeys: ["meta_ads", "google_ads"] },
  { id: "campaigns",   name: "Launch a Campaign",     icon: Rocket,     blurb: "Course and gear launches across every channel.", toolKeys: ["course", "gear"] },
  { id: "sales",       name: "Build a Sales Funnel",  icon: Layout,     blurb: "Sales funnel copy and full branded landing / sales pages.", toolKeys: ["funnel", "html_studio"] },
  { id: "seo",         name: "Grow Search Traffic",   icon: TrendingUp, blurb: "SEO blogs, product-page rewrites and branded web pages.", toolKeys: ["blog", "seo_product", "html_studio"] },
  { id: "brand",       name: "Build Your Brand",      icon: Award,      blurb: "Handle reviews and pitch brand partnerships.", toolKeys: ["review", "brand_deal"] },
];

export function toolsForCategory(catId) {
  const cat = CATEGORIES.find((c) => c.id === catId);
  if (!cat) return [];
  return cat.toolKeys.map((k) => TOOLS.find((t) => t.key === k)).filter(Boolean);
}

export const HERO_DESKTOP = "https://customer-assets.emergentagent.com/job_dive-media-os/artifacts/we4ar1sc_GPTempDownload%20250.JPG";
export const HERO_MOBILE = "https://customer-assets.emergentagent.com/job_dive-media-os/artifacts/tc68esg5_GPTempDownload%20251.JPG";
export const FOUNDER_IMG = "https://customer-assets.emergentagent.com/job_dive-media-os/artifacts/adejqjah_GPTempDownload%20249.JPG";
export const SCENIC_IMG = "https://customer-assets.emergentagent.com/job_dive-media-os/artifacts/64hgmx5c_GPTempDownload%20254.JPG";

// Canonical DMS Brand Kit Prompt (Feb 2026). Paste-and-go prompt users run
// in their existing ChatGPT / Claude / Gemini chat — it silently uses all
// existing conversation context, asks only for genuinely missing pieces,
// then emits a professionally-formatted .docx that the user uploads back
// into DMS. Same prompt for creators + businesses — it self-adapts.
const DMS_BRAND_KIT_PROMPT = `You are an expert brand strategist specialising in scuba diving, adventure businesses and personal/service brands.
Your task is to build a complete, commercially useful Brand Guide for my diving business.

IMPORTANT: This is a context-first exercise, not a blank-slate interview.
Before asking me anything, use ALL relevant information already available to you about my business from:
- this conversation
- previous conversation context or memory available to you
- information I have already supplied during onboarding
- uploaded documents or materials available to you
- reliable current public information, if web research is available and useful

Do not ask me to repeat information you already know.

STEP 1 — BUILD A WORKING PROFILE SILENTLY
First, internally draft the best possible answer to all ten areas below using existing information.
For each area, classify the information as:
CONFIRMED — clearly established from information I have provided or reliable evidence.
INFERRED — strongly suggested by the available context, but not explicitly confirmed.
MISSING — insufficient information to answer accurately.
CONFLICTING — multiple pieces of information disagree.
Do not show me this internal classification unless it is useful for resolving a question.
Never invent facts simply to complete the guide.

STEP 2 — GAP-FILLING INTERVIEW
Only ask me questions where the missing, inferred or conflicting information would materially affect the accuracy or usefulness of the final Brand Guide.
Do NOT ask questions simply because they appear in the list below.
Skip any question that can already be answered confidently.
Ask only ONE focused question at a time.
Where useful, briefly tell me what you already understand before asking for the missing detail.
Example:
"I already understand that you operate recreational, technical and CCR training in Malta. The only thing I need to confirm here is the legal company name. What is the registered legal entity?"
Whenever possible, make questions easy to answer by presenting a likely answer for confirmation rather than forcing me to start from scratch.
For example:
"Based on everything I know, I would describe your core transformation as: 'Helping divers progress from curiosity and initial certification towards confidence, competence and increasingly advanced diving.' Would you keep this, change it, or sharpen it?"
If something is useful but non-essential, make a reasonable evidence-based inference rather than extending the interview unnecessarily.
Prioritise completing a strong guide efficiently.

AREAS TO COVER

1. BUSINESS IDENTITY
Establish:
- legal business name
- trading/brand name

2. LOCATION & OPERATING ENVIRONMENT
Establish:
- country
- region/base
- primary diving locations
- important local dive environments, sites or geographic areas relevant to the brand

3. CORE TRANSFORMATION
Identify the single most important transformation the business delivers.
Do not confuse the service with the transformation.
Bad: "We teach scuba diving."
Better: "We turn curious beginners into confident independent divers."
- ability
- confidence
- identity
- experience
- access
- progression

4. IDEAL CUSTOMERS
Develop THREE distinct ideal customer profiles.
Describe each as a real person with:
- current situation
- diving experience
- motivation
- problem or frustration
- aspiration
- what they value when choosing a dive centre
- likely objections or fears
- what would make them choose this business
Do not reduce personas to demographics such as: "Men aged 30–45."
Demographics may support the persona, but motivations and behaviour matter more.

5. OFFERS & SERVICES
Identify the main commercial offer categories rather than creating an unnecessarily long product catalogue.
For each category establish:
- offer/service
- who it is for
- typical outcome
- approximate price or price range where known
- role within the customer journey
Where appropriate, organise offers into a progression ladder.
Do not invent prices. If pricing changes regularly, use an approximate current range and label it accordingly.

6. COMPETITIVE POSITION
Identify the THREE competitors most relevant to the business.
Competitors should be selected based on genuine customer overlap, not merely geographic proximity.
For each competitor consider:
- where they compete
- what they are strong at
- why a customer might choose them
- where this brand is meaningfully differentiated
Then identify the strongest defensible differentiation for this business.
Do not use empty claims such as "better service", "best instructors", "highest quality" unless supported by evidence.
Look instead for structural advantages such as: training depth, progression pathways, specialist expertise, location, infrastructure, facilities, boats, equipment, technical capability, CCR capability, instructor development, heritage, community, customer experience, operating philosophy.

7. BRAND VALUES
Select 3–5 values that genuinely influence how the business behaves.
Avoid generic corporate words unless they have practical meaning.
For every value, explain briefly what that value looks like in practice.
For example:
SAFETY — We prioritise sound judgement, conservative decision-making, equipment standards and quality training over simply completing a dive or certification.

8. VOICE & TONE
Define the brand voice using THREE primary adjectives.
Then expand this into practical writing guidance covering:
- how the brand sounds
- how technical it should be
- how it speaks to beginners
- how it speaks to experienced or technical divers
- attitude towards selling
- level of humour
- use of jargon
- confidence versus humility
Identify THREE things the brand would never say.
These should be realistic examples that help AI systems understand the boundaries of the voice.
Also create one concise voice principle, such as:
"Expertise without ego. Adventure without hype."

9. PROOF & CREDIBILITY
Collect evidence that supports the positioning.
Consider: certifications/accreditations, training agency status, years operating, instructor qualifications, students trained, dives completed, reviews, testimonials, awards, facilities, equipment, boats, technical infrastructure, gas blending, milestones, partnerships, environmental/community involvement, notable achievements.
Distinguish hard proof from general marketing claims. Do not fabricate statistics.
If a useful figure is unknown but not essential, omit it or label it as needing confirmation.

10. LONG-TERM POSITIONING
Develop a clear one-sentence positioning statement based on:
"We help [specific audience] do/achieve [specific transformation] so that [meaningful outcome]."
The statement should accommodate where the business is going, not merely describe its current services.
Also create:
- a shorter positioning line
- one central brand idea or strategic concept if a strong one emerges naturally from the evidence
Do not force a slogan if the strategy does not justify one.

FINAL SYNTHESIS
Once all MATERIAL gaps have been resolved, stop asking questions.
Do not wait for every tiny factual detail if the guide can already be completed accurately.
Synthesize everything into a professional Brand Guide using these sections:
1. Business
2. Positioning
3. Audience
4. Offers
5. Differentiation
6. Voice & Tone
7. Values
8. Proof
Within Positioning include: core transformation, long-term positioning statement, short positioning line, central brand idea (if appropriate).
Within Audience include the three detailed customer personas.
Within Offers show the customer journey/progression ladder wherever appropriate.
Within Voice & Tone include practical instructions that another AI could use to consistently write in the brand's voice.

QUALITY STANDARD
The final guide must be:
- specific rather than generic
- commercially useful
- grounded in evidence
- internally consistent
- concise enough to remain usable
- detailed enough that another AI can understand the business without additional explanation
- written in UK English
- free from empty marketing clichés
- suitable for use as persistent context inside Dive Media Strategist
Where there is uncertainty:
- do not silently convert an assumption into fact
- either resolve it during the interview or phrase it appropriately
- prioritise strategic truth over filling every field

FINAL DELIVERABLE
After the Brand Guide is complete, create it as a professionally formatted Microsoft Word document (.docx) available for download.
Use the filename: [Trading Name] - Brand Guide.docx
The document should have:
- a clean professional title page
- clear heading hierarchy
- readable paragraph spacing
- concise tables only where they improve understanding
- no unnecessary decorative elements
- formatting suitable for both human use and later AI reference
Also provide a brief message confirming that the Brand Guide is complete and provide the downloadable .docx file.
Do not output the entire guide in chat unless I explicitly request the plain-text version as well.`;

export const BRAND_GUIDE_PROMPTS = {
  business: DMS_BRAND_KIT_PROMPT,
  creator: DMS_BRAND_KIT_PROMPT,
};

// Backwards-compat: default export used across the codebase.
export const BRAND_GUIDE_PROMPT = BRAND_GUIDE_PROMPTS.business;

// Identity classification helper - creator vs business.
export function identityMode(identity) {
  if (!identity) return "creator";
  const s = String(identity).toLowerCase();
  if (s.includes("centre") || s.includes("center") || s.includes("shop") || s.includes("brand")) return "business";
  if (s.includes("instructor")) return "business";
  return "creator";
}

// Identity-matched onboarding examples for weak/strong pairs.
// Keys: identity value from step 1. Fallback: "default".
export const ONBOARDING_EXAMPLES = {
  "Recreational Diver": {
    niche: { weak: "I dive on holidays.", strong: "AOWD recreational diver, focused on Mediterranean day-boat diving in Malta and Gozo, sharing what I wish someone had told me on dive 20 to 100." },
    audience: { weak: "Other divers.", strong: "Certified Open Water divers, 28-45, who dive 2-3 weeks a year on holiday and want to feel more competent between trips." },
    personality: { weak: "Chill and friendly.", strong: "Peer-to-peer, honest about my own mistakes, never lecturing. I talk like a diver at a bar, not a course director in a classroom." },
    topics: { weak: "My holidays and cool marine life.", strong: "Trim and buoyancy on the fly, gear that survived 5+ trips, mistakes on my first 50 dives, dive-buddy communication, packing for warm-water trips." },
  },
  "Freediver": {
    niche: { weak: "I freedive.", strong: "AIDA 3 pool + depth freediver based in Dahab, sharing safe self-training routines, equalisation fixes, and the psychological work behind depth progression." },
    audience: { weak: "Freedivers.", strong: "Pool-certified freedivers, 25-40, who want to progress to 30m+ safely without a full-time coach in their city." },
    personality: { weak: "Calm.", strong: "Calm, deliberate, slightly clinical. I never oversell depth. I speak to viewers who take breath-hold seriously." },
    topics: { weak: "Freediving tips.", strong: "Frenzel drills, mouthfill, safe self-training, mental training for depth, injury prevention, gear at each depth range." },
  },
  "Technical Diver": {
    niche: { weak: "I do tech diving.", strong: "Full-cave and normoxic trimix diver in Mexico. Content focused on the mindset transition from recreational to overhead environments, not the certifications themselves." },
    audience: { weak: "Advanced divers.", strong: "Divers with 100+ logged dives, 30-50, who feel stuck in AOWD-Deco land and want to understand what real overhead diving actually asks of them." },
    personality: { weak: "Serious.", strong: "Precise, unglamorous, low-ego. I show the boring parts on purpose. I never romanticise depth or overhead." },
    topics: { weak: "Cave diving.", strong: "Team protocols, valve drills, cave line etiquette, gas management math, dry-run rehearsal, why sidemount changes psychology, real cave accident breakdowns." },
  },
  "Dive Instructor": {
    niche: { weak: "I teach diving.", strong: "SSI Advanced Open Water Instructor in the Red Sea, focused on rebuilding buoyancy and trim in already-certified divers who never got taught it properly." },
    audience: { weak: "New students.", strong: "Divers who got certified elsewhere years ago, 30-55, who feel embarrassed by their fin kicks and want a low-pressure refresher without redoing an open water course." },
    personality: { weak: "Patient.", strong: "Warm, direct, calmly technical. I never shame a student's original training. I fix in private, celebrate in public." },
    topics: { weak: "Teaching diving.", strong: "Buoyancy fixes without gear changes, common trim mistakes and their real cause, how to breathe less, why weighting is the root of everything, my most reused pool drills." },
  },
  "Dive Centre": {
    niche: { weak: "I run a dive centre.", strong: "SSI dive centre on Gozo focused on small-group AOWD, Nitrox, and photography trips for UK repeat guests. Twin-boat operation, max 8 divers per boat." },
    audience: { weak: "Tourists.", strong: "Open Water divers aged 30-55 from the UK, planning a return Malta/Gozo trip with a partner or friend, who want small-group diving and a boat that isn't packed." },
    personality: { weak: "Professional.", strong: "Warm, un-pushy, borderline understated. We let the small-group experience speak. We never post 'book now!' style content." },
    topics: { weak: "Dive site posts.", strong: "Site conditions this week, our small-boat routine, guest-of-the-week transformation, gear rental honesty, why we cap groups, seasonal marine life, quiet-water alternatives." },
  },
  "Dive Shop": {
    niche: { weak: "I sell dive gear.", strong: "Independent scuba shop in the UK specialising in cold-water gear and honest gear reviews. We fit dry suits properly and don't push brand deals." },
    audience: { weak: "Divers.", strong: "UK-based divers, 30-60, quarry- and coast-diving in cold water, tired of gear reviews that are secretly ads." },
    personality: { weak: "Helpful.", strong: "Unshowy, technical, calmly opinionated. We name specific product weaknesses. We refuse gear that fails our own dives." },
    topics: { weak: "New products.", strong: "Dry-suit fit walk-throughs, cold-water regulator picks, gear that failed us and why, service intervals nobody talks about, real cost of ownership, quarry-vs-sea kit lists." },
  },
  "Dive Creator": {
    niche: { weak: "I make diving videos.", strong: "Diving creator on Instagram focused on Mediterranean wreck diving and reef ID, blending short cinematic reels with practical dive-planning breakdowns." },
    audience: { weak: "My followers.", strong: "AOWD divers, 25-40, planning European dive trips, who love the visuals but stay for practical planning and honest site reviews." },
    personality: { weak: "Adventurous.", strong: "Curious, calmly cinematic, self-deprecating about my own screw-ups on camera. I never fake danger." },
    topics: { weak: "Diving content.", strong: "Wreck walk-throughs, planning a Mediterranean trip on a budget, my current-year kit, editing workflow, honest site reviews, ID mistakes I've made." },
  },
  "Dive Brand": {
    niche: { weak: "We sell dive products.", strong: "Independent freediving brand making minimalist, cold-water-first freediving suits. Every video is either athlete-led, engineering-led, or ocean-led. Never lifestyle." },
    audience: { weak: "Divers.", strong: "Freedivers 25-45 in Northern Europe, training in 10-16C water year-round, who value long-life gear over seasonal drops." },
    personality: { weak: "Cool.", strong: "Unhurried, engineering-first, quietly premium. We never use hype. We never do influencer bundles." },
    topics: { weak: "Product features.", strong: "Neoprene panel deep-dives, athlete training weeks, factory process, environmental cost breakdowns, cold-water routines, longevity data on our own suits after 3-4 years." },
  },
  default: {
    niche: { weak: "I dive.", strong: "AOWD recreational diver focused on Mediterranean day-boat diving, sharing what I wish someone had told me on dive 20 to 100." },
    audience: { weak: "Divers.", strong: "Certified Open Water divers, 28-45, who dive 2-3 weeks a year and want to feel more competent between trips." },
    personality: { weak: "Friendly and fun.", strong: "Calm, peer-to-peer, honest about my own mistakes. I talk like a diver at a bar, not a course director." },
    topics: { weak: "Diving and travel.", strong: "Trim and buoyancy on the fly, gear that survived 5+ trips, dive-buddy communication, packing for warm-water trips." },
  },
};
